import { AnyExtension, CommandProps, Editor } from '@tiptap/core';
import { EditorState, TextSelection, Transaction } from '@tiptap/pm/state';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import { BulletList, OrderedList, ListItem } from '@tiptap/extension-list';
import { Document } from '@tiptap/extension-document';
import Link from '@tiptap/extension-link';
import HardBreak from '@tiptap/extension-hard-break';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import { Markdown } from '@tiptap/markdown';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import SlotToken, {
  replaceSlotTokensWithSpans,
  slotTokenText
} from './SlotToken';

export interface MarkdownCapableEditor {
  getMarkdown: () => string;
}

export type OutputFormat = 'markdown' | 'plaintext';

export const createMarkdownExtensions = (
  placeholder: string
): AnyExtension[] => [
  Markdown.configure({
    markedOptions: { breaks: true }
  }),
  Document,
  Paragraph,
  Text,
  Bold,
  Italic,
  OrderedList,
  BulletList,
  ListItem,
  UndoRedo,
  Link,
  HardBreak.configure({
    keepMarks: false
  }),
  SlotToken,
  Placeholder.configure({
    placeholder
  })
];

export const createPlaintextExtensions = (
  placeholder: string
): AnyExtension[] => [
  Document,
  Paragraph,
  Text,
  UndoRedo,
  HardBreak.configure({
    keepMarks: false
  }),
  SlotToken,
  Placeholder.configure({
    placeholder
  })
];

export type EditorListType = 'bulletList' | 'orderedList';

const HARD_BREAK_NODE = 'hardBreak';
const TOP_LEVEL_DEPTH = 1;
const BACKWARD_ASSOC = -1;
const FORWARD_ASSOC = 1;

const lineBoundaryBreakPositions = (tr: Transaction): number[] => {
  const { $from, $to, from, to } = tr.selection;
  if ($from.depth !== TOP_LEVEL_DEPTH || !$from.sameParent($to)) return [];

  const paragraphStart = $from.start();
  const breakPositions: number[] = [];
  $from.parent.forEach((child, offset) => {
    if (child.type.name === HARD_BREAK_NODE) {
      breakPositions.push(paragraphStart + offset);
    }
  });

  const beforeLine = breakPositions.filter((pos) => pos < from).pop();
  const afterLine = breakPositions.find((pos) => pos >= to);
  return [afterLine, beforeLine].filter(
    (pos): pos is number => pos !== undefined
  );
};

const splitLineAroundSelection = ({ tr }: CommandProps): boolean => {
  const breakPositions = lineBoundaryBreakPositions(tr);
  if (breakPositions.length > 0) {
    let cursor = tr.selection.from;
    breakPositions.forEach((pos) => {
      // Splits before the cursor must push it into the new (current-line)
      // paragraph; splits after must keep it in the current one.
      const assoc = pos < cursor ? FORWARD_ASSOC : BACKWARD_ASSOC;
      const stepCountBeforeSplit = tr.steps.length;
      tr.delete(pos, pos + 1);
      tr.split(pos);
      cursor = tr.mapping.slice(stepCountBeforeSplit).map(cursor, assoc);
    });
    tr.setSelection(TextSelection.create(tr.doc, cursor));
  }
  return true;
};

export const toggleListOnCurrentLine = (
  editor: Editor,
  listType: EditorListType
): boolean => {
  const chain = editor.chain().focus().command(splitLineAroundSelection);
  return listType === 'bulletList'
    ? chain.toggleBulletList().run()
    : chain.toggleOrderedList().run();
};

const LIST_ITEM_NODE = 'listItem';

interface EditorViewLike {
  state: EditorState;
  dispatch: (tr: Transaction) => void;
}

const selectionInListItem = (state: EditorState): boolean => {
  const { $from } = state.selection;
  return (
    $from.node(-1)?.type.name === LIST_ITEM_NODE ||
    $from.node(-2)?.type.name === LIST_ITEM_NODE
  );
};

export const handleMarkdownEnter = ({
  state,
  dispatch
}: EditorViewLike): boolean => {
  if (selectionInListItem(state)) return false;

  const { $from, from, empty } = state.selection;
  const { tr } = state;
  const nodeBefore = empty ? $from.nodeBefore : null;

  if (nodeBefore?.type.name === HARD_BREAK_NODE) {
    const breakPos = from - nodeBefore.nodeSize;
    tr.delete(breakPos, from);
    tr.split(breakPos);
  } else {
    const hardBreak = state.schema.nodes[HARD_BREAK_NODE];
    if (!hardBreak) return false;
    tr.replaceSelectionWith(hardBreak.create());
  }

  dispatch(tr);
  return true;
};

export const handlePlaintextEnter = ({
  state,
  dispatch
}: EditorViewLike): boolean => {
  const { hardBreak } = state.schema.nodes;
  if (!hardBreak) return false;
  dispatch(state.tr.replaceSelectionWith(hardBreak.create()));
  return true;
};

const NEWLINE = '\n';
const NEWLINE_CHARS: ReadonlySet<string> = new Set([NEWLINE]);
const LINE_PADDING_CHARS: ReadonlySet<string> = new Set([' ', '\t']);

const trimTrailingChars = (
  text: string,
  chars: ReadonlySet<string>
): string => {
  let end = text.length;
  while (end > 0 && chars.has(text[end - 1])) end -= 1;
  return text.slice(0, end);
};

const stripLineTrailingPadding = (text: string): string =>
  text
    .split(NEWLINE)
    .map((line) => trimTrailingChars(line, LINE_PADDING_CHARS))
    .join(NEWLINE);

export const serializeMarkdown = (editor: MarkdownCapableEditor): string =>
  trimTrailingChars(
    stripLineTrailingPadding(editor.getMarkdown().replace(/\\\n/g, '\n')),
    NEWLINE_CHARS
  );

export const transformTokenMarkup = (
  input: string,
  format?: string
): string => {
  if (!input) return '';
  if (input.includes('data-type="slotToken"')) return input;

  const result = replaceSlotTokensWithSpans(input.replace(/\\\n/g, '\n'));

  if (format === 'plaintext' || format === 'markdown') {
    return result.replace(/\n/g, '<br>');
  }

  return result;
};

export const getPlaintextWithLineBreaks = (editor: Editor): string => {
  let result = '';

  editor.state.doc.descendants((node: ProseMirrorNode) => {
    const { name } = node.type;

    if (name === 'text') {
      result += node.text;
    } else if (name === 'slotToken') {
      result += slotTokenText(node.attrs);
    } else if (name === 'hardBreak') {
      result += '\n';
    } else if (name === 'paragraph' && result && !result.endsWith('\n')) {
      result += '\n';
    }
  });

  return trimTrailingChars(result, NEWLINE_CHARS);
};

export const readEditorContent = (
  editor: Editor,
  outputFormat: OutputFormat
): string =>
  outputFormat === 'plaintext'
    ? getPlaintextWithLineBreaks(editor)
    : serializeMarkdown(editor);

export const setEditorContent = (
  editor: Editor,
  content: string,
  outputFormat: OutputFormat
): void => {
  if (outputFormat === 'markdown') {
    editor.commands.setContent(content, {
      contentType: 'markdown',
      emitUpdate: false
    });
  } else {
    editor.commands.setContent(transformTokenMarkup(content, outputFormat), {
      emitUpdate: false
    });
  }
};

export const insertPlaintextWithHardBreaks = (
  editor: Editor,
  content: string
): void => {
  if (!content.includes('<br')) {
    editor.commands.setContent(content, { emitUpdate: false });
    return;
  }

  editor.commands.clearContent();
  const parts = content.split(/<br\s*\/?>/i);
  parts.forEach((part, index) => {
    if (part.trim()) {
      editor.commands.insertContent(part);
    }
    if (index < parts.length - 1) {
      editor.commands.setHardBreak();
    }
  });
};
